import { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { format } from "date-fns";

const DateTable = (props) => {
  const [modules, setModules] = useState([]);

  function translateModuleName(code) {
    if (!code) return code;

    // PLOMB
    if (code === "PLOMB") return "Plomb";

    // R-SST
    if (code === "R-SST") return "Recyclage SST";

    // AIPR-XXX
    if (code.startsWith("AIPR")) {
      const suffix = code.split("-")[1];

      const aiprmapping = {
        CCP: "Concepteur",
        ECD: "Encadrant",
        OPE: "Opérateur",
        EXM: "Examen",
      };

      return aiprmapping[suffix] || code;
    }

    // RGE ou autres → laisser tel quel
    if (code.startsWith("ELEC")) return code;

    // ---------------------------
    // CAS SS3 / SS4
    // ---------------------------
    if (code.includes("SS3") || code.includes("SS4")) {
      const prefix = code.split("-")[0];

      const letter = prefix[0];
      const end = prefix.slice(-2);

      // 1) Traduction du début
      let startLabel = "";
      if (letter === "P") startLabel = "Personnel";
      else if (letter === "Q") startLabel = "1er Recyclage";
      else if (letter === "R") startLabel = "Recyclage à 3 ans";
      else startLabel = prefix; // fallback

      // 2) Traduction des 2 dernières lettres
      const endMap = {
        EC: "Encadrant de chantier",
        ET: "Encadrant technique",
        OC: "Opérateur de chantier",
        OI: "Opérateur de chantier",
        MI: "Encadrant cumul de fonction",
      };

      const endLabel = endMap[end] || end;

      return `${startLabel} ${endLabel}`;
    }

    return code;
  }

  // Ordre d'affichage des modules par type de formation, aligné sur l'ordre
  // des accordéons de la page de la formation correspondante.
  const MODULE_ORDER = {
    SS3: [
      "Personnel Opérateur de chantier",
      "Personnel Encadrant de chantier",
      "Personnel Encadrant technique",
      "1er Recyclage Opérateur de chantier",
      "1er Recyclage Encadrant de chantier",
      "1er Recyclage Encadrant technique",
      "Recyclage à 3 ans Opérateur de chantier",
      "Recyclage à 3 ans Encadrant de chantier",
      "Recyclage à 3 ans Encadrant technique",
    ],
    SS4: [
      "Personnel Opérateur de chantier",
      "Personnel Encadrant de chantier",
      "Personnel Encadrant technique",
      "Personnel Encadrant cumul de fonction",
      "Recyclage à 3 ans Opérateur de chantier",
      "Recyclage à 3 ans Encadrant de chantier",
      "Recyclage à 3 ans Encadrant technique",
      "Recyclage à 3 ans Encadrant cumul de fonction",
    ],
    AIPR: ["Concepteur", "Encadrant", "Opérateur", "Examen"],
  };

  function getModuleSortIndex(formation, moduleName) {
    const order = MODULE_ORDER[formation];
    if (!order) return -1;
    const idx = order.indexOf(moduleName);
    return idx === -1 ? order.length : idx;
  }

  useEffect(() => {
    const fetchDatesFromBackend = async () => {
      try {
        const res = await fetch(
          `https://prevdocs-backend.onrender.com/sessions/next_dates_sessions/${props.formation}`,
        );

        const sessions = await res.json();

        const grouped = {};

        sessions.forEach((s) => {
          const full = s.codeProduit || "";
          const moduleName = translateModuleName(full);
          // Si SS3 ou SS4, max 10, si SPS max 12 pour le reste on garde s.effectifMaxiSession
          var maxEffectifSession = 0;
          if (full.includes("SS3") || full.includes("SS4")) {
            maxEffectifSession = 10;
          } else if (full.includes("SPS")) {
            maxEffectifSession = 12;
          } else {
            maxEffectifSession = s.effectifMaxiSession;
          }

          if (!grouped[moduleName]) grouped[moduleName] = [];

          const rawPlaces =
            maxEffectifSession - (s.vueSessionsNbInscrits_nbInscrits || 0);

          grouped[moduleName].push({
            id: s.numSession,
            debut: new Date(s.dateDebutSession),
            fin: new Date(s.dateFinSession),
            nbPlaces: Math.max(0, rawPlaces),
          });
        });

        // Format comme ton ancienne structure
        const formatted = Object.keys(grouped)
          .map((mod) => ({
            moduleName: mod,
            dates: grouped[mod].sort((a, b) => a.debut - b.debut),
          }))
          .sort(
            (a, b) =>
              getModuleSortIndex(props.formation, a.moduleName) -
              getModuleSortIndex(props.formation, b.moduleName)
          );

        // Si meme dates et meme nombre de places, ne garder qu'une ligne
        formatted.forEach((mod) => {
          const uniqueDates = {};
          mod.dates.forEach((date) => {
            const key = `${date.debut.toISOString()}_${date.fin.toISOString()}_${date.nbPlaces}`;
            uniqueDates[key] = date;
          });
          mod.dates = Object.values(uniqueDates);
        });

        setModules(formatted);
      } catch (err) {
        console.error("Erreur API :", err);
      }
    };

    fetchDatesFromBackend();
  }, [props.formation]);

  const formatDate = (date) => format(date, "dd/MM/yyyy");

  return (
    <div style={{ maxWidth: "600px", overflowX: "auto" }}>
      <h3>Prochaines dates</h3>

      {modules.every((m) => m.dates.length === 0) ? (
        <p>Nous contacter pour plus d'informations</p>
      ) : (
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th className="text-center">Module</th>
              <th className="text-center">Date de début</th>
              <th className="text-center">Date de fin</th>
              <th className="text-center">Places disponibles</th>
            </tr>
          </thead>

          <tbody>
            {modules.map((module) =>
              module.dates.map((item, idx) => (
                <tr key={item.id}>
                  {idx === 0 && (
                    <td
                      rowSpan={module.dates.length}
                      className="text-center"
                      style={{ fontWeight: "bold" }}
                    >
                      {module.moduleName}
                    </td>
                  )}
                  <td className="text-center">{formatDate(item.debut)}</td>
                  <td className="text-center">{formatDate(item.fin)}</td>
                  <td className="text-center">{item.nbPlaces}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default DateTable;